import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Square, Building2 } from "lucide-react";
import { formatTimeInTz } from "@/lib/timeFormat";
import { ChecklistPanel, PreShiftChecklistPanel } from "@/components/clock/ChecklistPanel";
import { DimensionSelector } from "@/components/clock/DimensionSelector";

/**
 * Live "you are clocked in" card. Ticks a running elapsed timer, shows
 * assigned dimensions, and hosts the interactive checklists for the
 * open entry.
 */
export function ClockCard({ entry, tz, worksite, onClockOut, busy }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.max(0, Math.floor((now - new Date(entry.clock_in_at).getTime()) / 1000));
  const hh = Math.floor(elapsed / 3600);
  const mm = Math.floor((elapsed % 3600) / 60);
  const ss = elapsed % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <Card className="card-elevate border-secondary/30" data-testid="open-punch-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-6">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-success" />
            You're clocked in
          </CardTitle>
          <CardDescription className="mt-1">
            Started at{" "}
            <span className="font-medium text-foreground">{formatTimeInTz(entry.clock_in_at, tz)}</span>
            {worksite && (
              <>
                {" "}· at{" "}
                <span className="font-medium text-foreground">{worksite.name}</span> ({worksite.code})
              </>
            )}
          </CardDescription>
          <div
            className="mt-4 font-display text-5xl font-semibold tabular-nums tracking-tight text-primary"
            data-testid="elapsed-timer"
          >
            {hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`}
          </div>
          <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">Elapsed</div>
        </div>
        <Button
          size="lg"
          variant="destructive"
          className="h-14 gap-2 px-6 text-base"
          onClick={onClockOut}
          disabled={busy}
          data-testid="clock-out-btn"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Square className="h-5 w-5" />}
          Clock out
        </Button>
      </CardHeader>
      <CardContent>
        {entry.dimension_values_resolved?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {entry.dimension_values_resolved.map((dv) => (
              <Badge key={dv.key} variant="secondary" className="rounded-full">
                <span className="text-muted-foreground">{dv.key}:</span>&nbsp;{dv.label} ({dv.code})
              </Badge>
            ))}
          </div>
        )}
        {entry.notes && <p className="mt-3 text-sm text-muted-foreground">Notes: {entry.notes}</p>}
        {(entry.checklist_completions || []).length > 0 && (
          <div className="mt-4 space-y-3" data-testid="active-checklists">
            {entry.checklist_completions.map((c) => (
              <ChecklistPanel key={c.checklist_id} entry={entry} completion={c} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Modal card for starting a shift: worksite + optional dimensions +
 * optional pre-shift checklists. Submits `POST /api/time/clock-in`.
 */
export function ClockInModal({ open, onClose, worksites, dimensions, onDone }) {
  const [worksiteId, setWorksiteId] = useState("");
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checklists, setChecklists] = useState([]);
  const [checklistsLoading, setChecklistsLoading] = useState(false);
  const [checkedByChecklist, setCheckedByChecklist] = useState({}); // { checklist_id: item_id[] }

  useEffect(() => {
    if (open) {
      setWorksiteId(worksites[0]?.id || "");
      setValues({});
      setNotes("");
      setError("");
      setChecklists([]);
      setCheckedByChecklist({});
    }
  }, [open, worksites]);

  // Fetch applicable checklists whenever worksite or dimension values change
  useEffect(() => {
    if (!open || !worksiteId) {
      setChecklists([]);
      return;
    }
    let cancelled = false;
    const fetchChecklists = async () => {
      setChecklistsLoading(true);
      try {
        const params = new URLSearchParams({ worksite_id: worksiteId });
        if (Object.keys(values).length > 0) {
          params.set("dimension_values", JSON.stringify(values));
        }
        const { data } = await api.get(`/time/resolve-checklists?${params.toString()}`);
        if (!cancelled) setChecklists(data.items || []);
      } catch (e) {
        if (!cancelled) setChecklists([]);
      } finally {
        if (!cancelled) setChecklistsLoading(false);
      }
    };
    fetchChecklists();
    return () => { cancelled = true; };
  }, [open, worksiteId, values]);

  const togglePreCheck = (checklistId, itemId, next) => {
    setCheckedByChecklist((prev) => {
      const current = new Set(prev[checklistId] || []);
      if (next) current.add(itemId);
      else current.delete(itemId);
      return { ...prev, [checklistId]: Array.from(current) };
    });
  };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const checklist_checks = Object.entries(checkedByChecklist)
        .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
        .map(([checklist_id, item_ids]) => ({ checklist_id, item_ids }));
      const body = {
        worksite_id: worksiteId,
        dimension_values: values,
        notes: notes || undefined,
      };
      if (checklist_checks.length > 0) body.checklist_checks = checklist_checks;
      await api.post("/time/clock-in", body);
      toast.success("Clocked in");
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  };

  const dimReady = dimensions.every((d) => !d.required || values[d.key]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="clock-in-modal" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Start your shift
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Worksite</Label>
            <Select value={worksiteId} onValueChange={setWorksiteId}>
              <SelectTrigger data-testid="clock-in-worksite">
                <SelectValue placeholder="Choose worksite" />
              </SelectTrigger>
              <SelectContent>
                {worksites.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name} ({w.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DimensionSelector
            dimensions={dimensions}
            values={values}
            onChange={(key, v) => setValues((prev) => ({ ...prev, [key]: v }))}
          />

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="clock-in-notes" />
          </div>

          {checklistsLoading && (
            <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="clock-in-checklists-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading checklists…
            </div>
          )}
          {!checklistsLoading && checklists.length > 0 && (
            <div className="space-y-3" data-testid="clock-in-checklists">
              <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                Pre-shift checklists ({checklists.length})
              </div>
              {checklists.map((cl) => (
                <PreShiftChecklistPanel
                  key={cl.id}
                  checklist={cl}
                  checkedIds={checkedByChecklist[cl.id] || []}
                  onToggle={togglePreCheck}
                />
              ))}
              <p className="text-xs text-muted-foreground">
                Optional — you can also tick items after you clock in.
              </p>
            </div>
          )}

          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !worksiteId || !dimReady} data-testid="clock-in-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Clock in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
